import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { BasecampLayout } from '../../components/Layout';
import { PageHeader, Card, Btn, Badge, Modal, Alert, Spinner, EmptyState, Input } from '../../components/UI';
import { MembershipSwitch, useMembershipToggle } from '../community/MemberModal';

const ROLE_LABELS = {
  IP: 'Ice Pilot',
  BP: 'Business Partner',
  CAND: 'Candidate',
  ADM: 'Administration',
  SH: 'Shareholder',
  SUPERADMIN: 'Super Admin',
  PM: 'Program Manager',
};

// Program Manager — podrole (čuvaju se u Funcs). Content Custodian ima svoje oblasti;
// prisutnost bilo koje oblasti = custodian. Tokeni moraju da odgovaraju backendu.
const PM_PLAIN_SUBROLES = ['Hike Organizer', 'Expedition Organization', 'Event Organizer'];
const CUSTODIAN_AREAS = [
  'Content Custodian: Influence and Effectiveness',
  'Content Custodian: Sales Excellence',
  'Content Custodian: Management and Leadership',
];
const ALL_PM_FUNCS = [...PM_PLAIN_SUBROLES, ...CUSTODIAN_AREAS];
const areaLabel = (a) => a.replace('Content Custodian: ', '');

// Kompaktan prikaz funkcija za tabelu (custodian oblasti se grupišu).
function displayFuncs(funcs) {
  const areas = funcs.filter(f => f.startsWith('Content Custodian: ')).map(areaLabel);
  const others = funcs.filter(f => !f.startsWith('Content Custodian: '));
  const out = [...others];
  if (areas.length) out.push(`Custodian: ${areas.join(', ')}`);
  return out;
}

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [allowedRoles, setAllowedRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // user object
  const [draftRoles, setDraftRoles] = useState([]);
  const [draftFuncs, setDraftFuncs] = useState([]);
  const [custodianOn, setCustodianOn] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [notice, setNotice] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await api.admin.getUsers();
      setUsers(data.users || []);
      setAllowedRoles(data.allowedRoles || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function openEdit(u) {
    setEditing(u);
    setDraftRoles(u.roles || []);
    setDraftFuncs(u.funcs || []);
    setCustodianOn((u.funcs || []).some(f => CUSTODIAN_AREAS.includes(f)));
  }

  function toggleRole(role) {
    setDraftRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  }

  function toggleFunc(name) {
    setDraftFuncs(prev =>
      prev.includes(name) ? prev.filter(f => f !== name) : [...prev, name]
    );
  }

  function toggleCustodian() {
    setCustodianOn(prev => {
      const next = !prev;
      if (!next) setDraftFuncs(f => f.filter(x => !CUSTODIAN_AREAS.includes(x))); // gasi → skini oblasti
      return next;
    });
  }

  async function saveRoles() {
    // Validacija: ako je Content Custodian uključen, bar jedna oblast mora.
    const isPM = draftRoles.includes('PM');
    if (isPM && custodianOn && !CUSTODIAN_AREAS.some(a => draftFuncs.includes(a))) {
      setError('Content Custodian requires at least one area (Influence and Effectiveness, Sales Excellence, or Management and Leadership).');
      return;
    }
    // Ako PM nije izabran, ne čuvamo nijednu PM podrolu (legacy funkcije se zadržavaju).
    const funcsToSave = isPM ? draftFuncs : draftFuncs.filter(f => !ALL_PM_FUNCS.includes(f));

    setSaving(true);
    setError(null);
    try {
      await api.admin.updateRoles(editing.id, draftRoles);
      await api.admin.updateFuncs(editing.id, funcsToSave);
      setUsers(prev => prev.map(u => u.id === editing.id ? { ...u, roles: draftRoles, funcs: funcsToSave } : u));
      setEditing(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function syncFromMicrosoft() {
    setSyncing(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api.admin.syncUsers();
      setNotice(`Synced from Microsoft: ${r.created} added, ${r.updated} updated (of ${r.total} members).`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  }

  async function toggleActive(u) {
    setError(null);
    try {
      const next = !u.isActive;
      await api.admin.setActive(u.id, next);
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, isActive: next } : x));
    } catch (err) {
      setError(err.message);
    }
  }

  // Član Basecamp-a da/ne — flag; isključena osoba ostaje isključena i posle Microsoft sync-a.
  const membership = useMembershipToggle({
    onChanged: (u, next) => { setError(null); setUsers(prev => prev.map(x => x.id === u.id ? { ...x, isMember: next } : x)); },
    onError: setError,
  });

  async function saveTarget(u) {
    const cur = u.pipelineTarget != null ? String(u.pipelineTarget) : '';
    const input = window.prompt(`Weighted-pipeline target for ${u.name || u.email} (Ice Pilot IDR — Explore "pipeline coverage").\nLeave empty to clear.`, cur);
    if (input === null) return; // cancel
    const trimmed = input.trim();
    const value = trimmed === '' ? null : Number(trimmed);
    if (value !== null && (!Number.isFinite(value) || value < 0)) { setError('Target must be a non-negative number.'); return; }
    setError(null);
    try {
      await api.admin.setTarget(u.id, value);
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, pipelineTarget: value } : x));
    } catch (err) {
      setError(err.message);
    }
  }

  async function deleteUser(u) {
    if (!window.confirm(`Permanently delete ${u.name || u.email}? This cannot be undone.`)) return;
    setError(null);
    try {
      await api.admin.deleteUser(u.id);
      setUsers(prev => prev.filter(x => x.id !== u.id));
    } catch (err) {
      setError(err.message);
    }
  }

  const filtered = users.filter(u => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  return (
    <BasecampLayout>
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '32px 24px' }}>
        <PageHeader
          title="User Management"
          subtitle="Overview of all users and management of their roles"
          action={
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant="outline" onClick={load}>Refresh</Btn>
              <Btn onClick={syncFromMicrosoft} loading={syncing}>Sync from Microsoft</Btn>
            </div>
          }
        />

        {notice && <div style={{ marginBottom: 16 }}><Alert type="success">{notice}</Alert></div>}
        {error && <div style={{ marginBottom: 16 }}><Alert type="error">{error}</Alert></div>}

        <div style={{ marginBottom: 16, maxWidth: 320 }}>
          <Input
            placeholder="Search by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner /></div>
        ) : filtered.length === 0 ? (
          <EmptyState title="No users" message="No users match your search." />
        ) : (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr>
                    {['Name', 'Email', 'Roles', 'Basecamp', 'Status', 'Pipeline target', ''].map((h, i) => (
                      <th key={i} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => (
                    <tr key={u.id} style={{ borderBottom: '1px solid var(--line)', opacity: u.isMember ? 1 : 0.6 }}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600 }}>{u.name || '—'}</div>
                        {!u.ssoLinked && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--ink-faint)' }}>hasn't signed in via Microsoft yet</div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, color: 'var(--ink-soft)' }}>{u.email || '—'}</td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {u.roles.length ? u.roles.map(r => (
                            <Badge key={r} status={r === 'SUPERADMIN' ? 'success' : 'default'}>{r}</Badge>
                          )) : <span style={{ fontSize: '0.72rem', color: 'var(--ink-faint)', fontStyle: 'italic' }}>no role — cannot sign in</span>}
                        </div>
                        {(u.funcs || []).length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                            {displayFuncs(u.funcs).map(f => (
                              <span key={f} style={{ fontSize: '0.66rem', padding: '1px 7px', borderRadius: '99px', border: '1px dashed var(--ink-faint)', color: 'var(--ink-faint)' }}>{f}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        <MembershipSwitch on={u.isMember} busy={membership.busyId === u.id} onToggle={() => membership.toggle(u)} compact />
                      </td>
                      <td style={tdStyle}>
                        <Badge status={u.isActive ? 'success' : 'default'}>{u.isActive ? 'Active' : 'Inactive'}</Badge>
                      </td>
                      <td style={tdStyle}>
                        <button
                          onClick={() => saveTarget(u)}
                          title="Set weighted-pipeline target (Ice Pilot IDR)"
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--ink)', textDecoration: 'underline', fontFamily: 'var(--font-body)', fontSize: '0.85rem' }}
                        >
                          {u.pipelineTarget != null ? u.pipelineTarget.toLocaleString() : '— set'}
                        </button>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Btn size="sm" variant="outline" onClick={() => openEdit(u)}>Roles</Btn>{' '}
                        <Btn size="sm" variant="ghost" onClick={() => toggleActive(u)}>
                          {u.isActive ? 'Deactivate' : 'Activate'}
                        </Btn>{' '}
                        <Btn size="sm" variant="danger" onClick={() => deleteUser(u)}>Delete</Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {membership.dialog}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={`Roles — ${editing?.name || ''}`}>
        <p style={{ fontSize: '0.85rem', color: 'var(--ink-soft)', marginBottom: 16 }}>
          Select which roles the user has. Roles determine what they see in the system.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          {allowedRoles.map(role => (
            <label key={role} style={{
              display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
              padding: '10px 12px', borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--line)',
              background: draftRoles.includes(role) ? 'var(--canvas-warm)' : 'transparent',
            }}>
              <input
                type="checkbox"
                checked={draftRoles.includes(role)}
                onChange={() => toggleRole(role)}
              />
              <span style={{ fontWeight: 600 }}>{role}</span>
              <span style={{ color: 'var(--ink-soft)', fontSize: '0.82rem' }}>{ROLE_LABELS[role] || ''}</span>
            </label>
          ))}
        </div>

        {draftRoles.includes('PM') && (
          <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', marginBottom: 20, background: 'var(--canvas)' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 4 }}>
              Program Manager — sub-roles
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--ink-faint)', marginBottom: 10 }}>
              Pick any that apply. Content Custodian requires at least one area.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {PM_PLAIN_SUBROLES.map(sr => (
                <label key={sr} style={subRoleLabelStyle(draftFuncs.includes(sr))}>
                  <input type="checkbox" checked={draftFuncs.includes(sr)} onChange={() => toggleFunc(sr)} />
                  <span>{sr}</span>
                </label>
              ))}
              <label style={subRoleLabelStyle(custodianOn)}>
                <input type="checkbox" checked={custodianOn} onChange={toggleCustodian} />
                <span>Content Custodian</span>
              </label>
              {custodianOn && (
                <div style={{ marginLeft: 22, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontSize: '0.76rem', color: 'var(--ink-faint)' }}>Choose at least one area:</div>
                  {CUSTODIAN_AREAS.map(area => (
                    <label key={area} style={subRoleLabelStyle(draftFuncs.includes(area))}>
                      <input type="checkbox" checked={draftFuncs.includes(area)} onChange={() => toggleFunc(area)} />
                      <span>{areaLabel(area)}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn variant="outline" onClick={() => setEditing(null)}>Cancel</Btn>
          <Btn onClick={saveRoles} loading={saving}>Save</Btn>
        </div>
      </Modal>
    </BasecampLayout>
  );
}

const thStyle = {
  padding: '8px 16px', textAlign: 'left',
  fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.08em',
  textTransform: 'uppercase', color: 'var(--muted)',
  borderBottom: '2px solid var(--ink-mid)', background: 'var(--canvas)',
};
const tdStyle = { padding: '12px 16px', verticalAlign: 'middle' };
const subRoleLabelStyle = (on) => ({
  display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
  padding: '8px 10px', borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--line)', fontSize: '0.88rem',
  background: on ? 'var(--canvas-warm)' : 'var(--canvas-white)',
});
